// components/CommentsSummary.js
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { TailSpin } from "react-loader-spinner";

import "../styles/CommentsSummary.css";

const CommentsSummary = ({
  reportId,
  spreadsheetId,
  report,
  onClose,
  clientEmail,
}) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchComments = useCallback(async () => {
    if (!reportId) {
      setError("No spreadsheet ID available");
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      // Build the URL with clientEmail if provided
      const url = clientEmail
        ? `/api/comments/${reportId}?clientEmail=${encodeURIComponent(
            clientEmail
          )}`
        : `/api/comments/${reportId}`;

      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (response.data.success) {
        setComments(response.data.comments);
      } else {
        setError(response.data.message || "Failed to load comments");
      }
    } catch (error) {
      console.error("Error fetching comments:", error);
      setError("Failed to load comments. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [reportId, clientEmail]); // Add clientEmail to dependencies

  // Update the useEffect dependencies
  useEffect(() => {
    fetchComments();
  }, [reportId, spreadsheetId, fetchComments, clientEmail]);

  const handleOpenSheet = (cellLocation) => {
    // Use the exact same logic as the footer button
    const baseSpreadsheetUrl = report?.reportMetadata?.isLocked
      ? report.reportMetadata.lockedSpreadsheetUrl
      : report?.link ||
        `https://docs.google.com/spreadsheets/d/${spreadsheetId}/edit`;

    // Remove any existing hash/fragment from the URL
    const cleanBaseUrl = baseSpreadsheetUrl.split("#")[0];

    // Format the cell location properly for Google Sheets
    let formattedCellLocation = cellLocation;

    // If we have a cell location with sheet name and cell reference
    if (cellLocation && cellLocation.includes("!")) {
      // Extract the parts
      const [sheetName, cellRef] = cellLocation.split("!");

      // Format the sheet name properly - wrap in single quotes if it contains spaces
      const formattedSheetName = sheetName.includes(" ")
        ? `'${sheetName}'`
        : sheetName;

      // Rebuild the cell location with proper formatting
      formattedCellLocation = `${formattedSheetName}!${cellRef}`;
    }

    // According to search results, the correct format is #range=RANGE
    // Sometimes adding gid=0 can help with navigation
    const sheetUrl = `${cleanBaseUrl}#range=${formattedCellLocation}`;

    // For debugging purposes
    console.log("Opening spreadsheet at:", sheetUrl);
    window.open(sheetUrl, "_blank");
  };

  const formatTimeAgo = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 7) {
      return date.toLocaleDateString();
    } else if (days > 0) {
      return `${days} day${days > 1 ? "s" : ""} ago`;
    } else if (hours > 0) {
      return `${hours} hour${hours > 1 ? "s" : ""} ago`;
    } else if (minutes > 0) {
      return `${minutes} minute${minutes > 1 ? "s" : ""} ago`;
    } else {
      return "just now";
    }
  };

  // Helper to highlight @mentions in the text
  const highlightMentions = (text) => {
    const parts = text.split(
      /(@[a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/g
    );
    return parts.map((part, index) =>
      part.match(/^@[a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+$/) ? (
        <span key={index} className="mention-highlight">
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  return (
    <div className="comments-panel">
      <div className="comments-panel-header">
        <h3>Sheet Comments</h3>
        <div className="header-actions">
          <button
            className="refresh-button"
            onClick={fetchComments}
            title="Refresh comments"
          >
            <i className="fas fa-sync-alt"></i>
          </button>
          <button className="close-button" onClick={onClose}>
            ×
          </button>
        </div>
      </div>

      <div className="comments-help">
        <div className="help-icon">
          <i className="fas fa-lightbulb"></i>
        </div>
        <div className="help-content">
          <p>
            <strong>How to comment in Google Sheets:</strong>
          </p>
          <ol>
            <li>Right-click on a cell and select "Comment"</li>
            <li>Type @ followed by an email to mention someone</li>
            <li>Click "Comment" to save</li>
          </ol>
          <p>Comments will appear here automatically.</p>
        </div>
      </div>

      <div className="comments-list">
        {loading ? (
          <div className="comments-loading">
            <TailSpin color="#0066ff" height={24} width={24} />
            <span>Loading comments...</span>
          </div>
        ) : error ? (
          <div className="comments-error">
            <i className="fas fa-exclamation-circle"></i>
            <p>{error}</p>
            <button className="retry-button" onClick={fetchComments}>
              Try Again
            </button>
          </div>
        ) : comments.length === 0 ? (
          <div className="comments-empty">
            <i className="fas fa-comments"></i>
            <p>No comments found in this spreadsheet.</p>
            <p className="comments-empty-sub">
              Add comments in Google Sheets to collaborate with others.
            </p>
          </div>
        ) : (
          comments.map((comment) => (
            <div
              key={comment.id}
              className={`comment-item ${
                comment.resolved ? "resolved" : "open"
              }`}
              onClick={() => handleOpenSheet(comment.cellLocation)}
            >
              <div className="comment-item-header">
                <div className="comment-author-info">
                  <div className="comment-author-avatar">
                    {comment.authorName.charAt(0).toUpperCase()}
                  </div>
                  <span className="comment-item-user">
                    {comment.authorName}
                  </span>
                </div>
                <span className="comment-item-date">
                  {formatTimeAgo(comment.createdTime)}
                </span>
              </div>

              <div className="comment-item-content">
                {highlightMentions(comment.content)}
              </div>

              <div className="comment-item-footer">
                <div className="comment-location">
                  <i className="fas fa-table"></i>
                  <span>
                    {comment.sheetName} - {comment.cellReference}
                  </span>
                </div>
                <div className="comment-status">
                  {comment.resolved ? (
                    <span className="status-resolved">
                      <i className="fas fa-check-circle"></i> Resolved
                    </span>
                  ) : (
                    <span className="status-open">
                      <i className="fas fa-comment"></i> Open
                    </span>
                  )}
                </div>
              </div>

              {comment.replies && comment.replies.length > 0 && (
                <div className="comment-replies">
                  <div className="reply-count">
                    <i className="fas fa-reply"></i> {comment.replies.length}{" "}
                    replies
                  </div>
                  <div className="latest-reply">
                    <span className="reply-author">
                      {comment.replies[0].authorName}:
                    </span>{" "}
                    {comment.replies[0].content.substring(0, 50)}
                    {comment.replies[0].content.length > 50 ? "..." : ""}
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      <div className="comments-panel-footer">
        <button
          className="open-spreadsheet-button"
          onClick={() => {
            // If report is locked, use the locked URL, otherwise use the original
            const spreadsheetUrl = report?.reportMetadata?.isLocked
              ? report.reportMetadata.lockedSpreadsheetUrl
              : report?.link ||
                `https://docs.google.com/spreadsheets/d/${spreadsheetId}/edit`;

            window.open(spreadsheetUrl, "_blank");
          }}
        >
          <i className="fas fa-external-link-alt"></i> Open in Google Sheets
        </button>
      </div>
    </div>
  );
};

export default CommentsSummary;
